import React from "react";
import {
  Users, Search, RefreshCw, FileDown, FileSpreadsheet,
  TrendingUp, Repeat, Building2, Mail, Phone, Loader2,
  ArrowDownAZ, ArrowUpAZ,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi, API } from "../lib/api";

function fmtDKK(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("da-DK", { style: "currency", currency: "DKK", maximumFractionDigits: 0 }).format(n);
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("da-DK", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const SORT_OPTIONS = [
  { k: "revenue_desc",  label: "Omsætning (højst først)" },
  { k: "revenue_asc",   label: "Omsætning (lavest først)" },
  { k: "bookings_desc", label: "Antal bookings (flest først)" },
  { k: "bookings_asc",  label: "Antal bookings (færrest først)" },
  { k: "recent_desc",   label: "Senest aktive" },
  { k: "oldest_desc",   label: "Ældste først" },
  { k: "name_asc",      label: "Navn (A → Å)" },
];

export default function CustomersPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  const [sort, setSort] = React.useState("revenue_desc");
  const [filter, setFilter] = React.useState("all"); // all | returning | business
  const [downloading, setDownloading] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await adminApi().get("/admin/customers");
      setData(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente kunder");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const downloadFile = async (format) => {
    setDownloading(format);
    try {
      const token = localStorage.getItem("ema_admin_token");
      const url = `${API}/admin/customers/export.${format}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const today = new Date().toISOString().slice(0, 10);
      const filename = `EMA_Kunder_${today}.${format}`;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`Hentet: ${filename}`);
    } catch (e) {
      toast.error(`Eksport fejlede: ${e.message}`);
    } finally {
      setDownloading(null);
    }
  };

  const filtered = React.useMemo(() => {
    if (!data?.items) return [];
    const q = search.trim().toLowerCase();
    let out = data.items.filter((c) => {
      if (filter === "returning" && !c.is_returning) return false;
      if (filter === "business" && !c.is_business) return false;
      if (!q) return true;
      return (
        (c.customer_email || "").toLowerCase().includes(q) ||
        (c.customer_name || "").toLowerCase().includes(q) ||
        (c.customer_phone || "").toLowerCase().includes(q)
      );
    });
    const cmp = {
      revenue_desc:  (a, b) => (b.total_revenue_dkk || 0) - (a.total_revenue_dkk || 0),
      revenue_asc:   (a, b) => (a.total_revenue_dkk || 0) - (b.total_revenue_dkk || 0),
      bookings_desc: (a, b) => (b.total_bookings || 0) - (a.total_bookings || 0),
      bookings_asc:  (a, b) => (a.total_bookings || 0) - (b.total_bookings || 0),
      recent_desc:   (a, b) => (b.last_booking_at || "").localeCompare(a.last_booking_at || ""),
      oldest_desc:   (a, b) => (a.first_booking_at || "").localeCompare(b.first_booking_at || ""),
      name_asc:      (a, b) => (a.customer_name || "").localeCompare(b.customer_name || "", "da"),
    }[sort];
    return [...out].sort(cmp);
  }, [data, search, sort, filter]);

  if (loading && !data) {
    return (
      <div className="bg-white border border-brand p-6 text-sm text-brand-text2" data-testid="customers-loading">
        Indlæser kunder…
      </div>
    );
  }
  if (!data) return null;

  const t = data.totals || {};

  return (
    <div className="space-y-4" data-testid="customers-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl text-brand-dark flex items-center gap-2">
            <Users size={22} className="text-brand-red" />
            Kunder
          </h2>
          <p className="text-xs text-brand-text2 mt-1">
            Unikke kunder grupperet på email · demo-bookinger ekskluderet
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={() => downloadFile("csv")}
            disabled={downloading !== null || !filtered.length}
            className="text-xs border border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white px-3 py-2 flex items-center gap-1 transition-colors disabled:opacity-60"
            data-testid="customers-export-csv"
          >
            {downloading === "csv" ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
            CSV
          </button>
          <button
            onClick={() => downloadFile("xlsx")}
            disabled={downloading !== null || !filtered.length}
            className="text-xs bg-emerald-700 text-white hover:bg-emerald-800 px-3 py-2 flex items-center gap-1 transition-colors disabled:opacity-60"
            data-testid="customers-export-xlsx"
          >
            {downloading === "xlsx" ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />}
            Excel
          </button>
          <button
            onClick={load}
            className="text-xs border border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white px-3 py-2 flex items-center gap-1 transition-colors"
            data-testid="customers-refresh"
          >
            <RefreshCw size={12} /> Opdater
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile
          label="Unikke kunder"
          value={t.total_customers || 0}
          icon={<Users size={14} className="text-brand-dark" />}
          accent="text-brand-dark"
          active={filter === "all"}
          onClick={() => setFilter("all")}
          testid="customers-tile-all"
        />
        <Tile
          label="Returkunder"
          value={t.returning_customers || 0}
          icon={<Repeat size={14} className="text-emerald-700" />}
          accent="text-emerald-700"
          active={filter === "returning"}
          onClick={() => setFilter(filter === "returning" ? "all" : "returning")}
          testid="customers-tile-returning"
        />
        <Tile
          label="Erhverv"
          value={t.business_customers || 0}
          icon={<Building2 size={14} className="text-blue-700" />}
          accent="text-blue-700"
          active={filter === "business"}
          onClick={() => setFilter(filter === "business" ? "all" : "business")}
          testid="customers-tile-business"
        />
        <Tile
          label="Total omsætning"
          value={fmtDKK(t.total_revenue_dkk || 0)}
          icon={<TrendingUp size={14} className="text-brand-red" />}
          accent="text-brand-red"
          small
          testid="customers-tile-revenue"
        />
      </div>

      {/* Search + sort */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-text2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søg på navn, email eller telefon…"
            className="w-full border border-brand pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-brand-dark"
            data-testid="customers-search"
          />
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="border border-brand px-3 py-2 text-sm bg-white focus:outline-none focus:border-brand-dark"
          data-testid="customers-sort"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.k} value={o.k}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Result count */}
      <div className="text-xs text-brand-text2">
        Viser <b className="text-brand-dark">{filtered.length}</b> af {data.items.length} kunder
        {filter !== "all" && <span> · filter: <b className="text-brand-dark uppercase">{filter}</b></span>}
        {search && <span> · søgning: "<b className="text-brand-dark">{search}</b>"</span>}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-brand p-6 text-sm text-brand-text2 text-center" data-testid="customers-empty">
          {data.items.length === 0
            ? "Ingen kunder fundet endnu — bookinger uden demo-flag dukker op her."
            : "Ingen kunder matcher din søgning."}
        </div>
      ) : (
        <div className="bg-white border border-brand overflow-x-auto" data-testid="customers-table">
          <table className="min-w-full text-sm">
            <thead className="bg-brand-dark text-white">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Kunde</th>
                <th className="text-left px-3 py-2 font-semibold">Telefon</th>
                <th className="text-right px-3 py-2 font-semibold">Bookings</th>
                <th className="text-right px-3 py-2 font-semibold">Betalt</th>
                <th className="text-right px-3 py-2 font-semibold">Omsætning</th>
                <th className="text-right px-3 py-2 font-semibold">Gns. ordre</th>
                <th className="text-left px-3 py-2 font-semibold">Første</th>
                <th className="text-left px-3 py-2 font-semibold">Sidste</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand">
              {filtered.map((c) => (
                <tr key={c.email_key} className="hover:bg-brand-bg/40" data-testid={`customer-row-${c.email_key}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-brand-dark truncate flex items-center gap-1.5">
                          {c.customer_name || "(uden navn)"}
                          {c.is_business && (
                            <span className="text-[10px] bg-blue-100 text-blue-900 border border-blue-300 px-1.5 py-0.5 uppercase font-bold" title="Erhvervskunde">
                              <Building2 size={9} className="inline -mt-0.5 mr-0.5" />B2B
                            </span>
                          )}
                          {c.is_returning && (
                            <span className="text-[10px] bg-emerald-100 text-emerald-900 border border-emerald-300 px-1.5 py-0.5 uppercase font-bold" title="Returkunde">
                              <Repeat size={9} className="inline -mt-0.5 mr-0.5" />Retur
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-brand-text2 flex items-center gap-1 truncate">
                          <Mail size={10} /> {c.customer_email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-brand-text2 whitespace-nowrap">
                    {c.customer_phone ? (
                      <a href={`tel:${c.customer_phone}`} className="hover:text-brand-red">
                        {c.customer_phone}
                      </a>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{c.total_bookings || 0}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-700">{c.paid_bookings || 0}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-brand-dark whitespace-nowrap">{fmtDKK(c.total_revenue_dkk)}</td>
                  <td className="px-3 py-2 text-right font-mono text-brand-text2 whitespace-nowrap">{fmtDKK(c.avg_order_value_dkk)}</td>
                  <td className="px-3 py-2 text-brand-text2 whitespace-nowrap">{fmtDate(c.first_booking_at)}</td>
                  <td className="px-3 py-2 text-brand-text2 whitespace-nowrap">{fmtDate(c.last_booking_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, icon, accent, onClick, active, testid, small }) {
  const clickable = !!onClick;
  const Comp = clickable ? "button" : "div";
  return (
    <Comp
      onClick={onClick}
      className={`text-left bg-white border p-4 transition-colors ${active ? "border-brand-dark shadow-sm" : "border-brand"} ${clickable ? "hover:border-brand-dark cursor-pointer" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="kicker">{label}</div>
        {icon}
      </div>
      <div className={`mt-2 ${small ? "text-xl" : "text-3xl"} font-display font-bold ${accent}`}>{value}</div>
    </Comp>
  );
}
